import { AxiosResponse } from 'axios';
import { useQuery } from 'react-query';

import { api } from '../../http';
import { DefaultRes } from '../../types';

export function useGenericQuery<ResultWithoutDefaultRes>(
    queryName: string,
    urlToHit: string,
    refetchInterval?: number,
    cacheTimeSeconds?: number,
    refetchOnWindowFocus = true,
    staleTimeSeconds?: number,
    // Set false to register the hook without fetching (keeps hook order
    // stable for callers that substitute local data, e.g. dev fixtures).
    enabled = true,
) {
    const makeGenericQuery = async () => {
        const { data } = await api.get<
            DefaultRes<ResultWithoutDefaultRes>,
            AxiosResponse<DefaultRes<ResultWithoutDefaultRes>>
        >(`/${urlToHit}`);

        return data.result;
    };

    return useQuery([queryName, urlToHit], makeGenericQuery, {
        refetchInterval: refetchInterval || false,
        // cacheTime: how long an inactive (no-observer) query entry stays in
        // the cache before being garbage-collected. Use `!== undefined` so
        // callers can pass 0 to fully evict on unmount — paired with
        // staleTime: 0 this gives a hard "no stale render" guarantee for
        // pages that switch customers / sessions.
        cacheTime: cacheTimeSeconds !== undefined ? (cacheTimeSeconds * (60 * 1000)) : (60 * (60 * 1000)),
        // staleTime: how long data is considered "fresh" before re-fetching.
        // Default to 5 minutes to prevent unnecessary re-fetches on navigation.
        // Use `!== undefined` so callers can pass 0 to force a fresh fetch on
        // every mount. NOTE: staleTime: 0 alone is stale-while-revalidate —
        // the cached value still renders for one frame while the refetch is
        // in flight. Combine with cacheTime: 0 (above) for a real cache bust.
        staleTime: staleTimeSeconds !== undefined ? (staleTimeSeconds * 1000) : (5 * 60 * 1000),
        refetchOnWindowFocus: refetchOnWindowFocus,
        enabled,
    });
}
